import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Mail, Users, Send, Loader2, Plus, Trash2, Paperclip, X, Download, RefreshCcw,
  CheckCircle2, XCircle, MinusCircle, Search, Pencil, Eye, FileText,
} from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { RichTextEditor } from './RichTextEditor';

const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

interface MerchantOption { id: number; companyName: string; brandName?: string | null; email: string; state?: string | null; city?: string | null; status: string }
interface Group {
  id: number; name: string; description?: string | null; states: string[]; merchantIds: number[];
  isActive: boolean; createdByName?: string | null; createdAt: string;
  memberCount: number; emailableCount: number; staleCount: number;
}
interface GroupDetail extends Group { members: MerchantOption[] }
interface Attachment { ref: string; fileName: string; mimetype?: string | null; sizeBytes?: number | null }
interface Template { id: number; groupId?: number | null; name: string; subject: string; bodyHtml: string; attachments?: Attachment[] | null; group?: { id: number; name: string } | null; updatedAt: string }
interface Campaign {
  id: number; campaignNo: string; groupId?: number | null; groupName?: string | null; subject: string;
  status: string; totalRecipients: number; sentCount: number; failedCount: number; skippedCount: number;
  attachments?: Attachment[] | null; attachmentBytes: number; errorMessage?: string | null;
  sentByName?: string | null; startedAt?: string | null; completedAt?: string | null; createdAt: string;
}
interface CampaignLog { id: number; merchantId?: number | null; merchantName?: string | null; email?: string | null; status: string; error?: string | null; sentAt?: string | null }

function fmtBytes(b?: number | null) {
  const n = Number(b ?? 0);
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDate(v?: string | null) {
  return v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
}

const TABS = [
  { id: 'send', label: 'Compose & Send', Icon: Send },
  { id: 'groups', label: 'Merchant Groups', Icon: Users },
  { id: 'templates', label: 'Templates', Icon: FileText },
  { id: 'log', label: 'Send Log', Icon: Mail },
] as const;

/**
 * Notifications — bulk merchant email.
 *
 * Flow: build a GROUP of merchants (filter by state, or hand-pick) → write the email (subject + HTML body
 * + attachments up to 20 MB) → send → read the per-merchant delivery log.
 */
export function NotificationsPage() {
  const [tab, setTab] = useState<string>('send');
  const { data: stats } = useQuery({
    queryKey: ['notif-stats'],
    queryFn: async () => (await api.get('/notifications/stats')).data as {
      campaigns: number; activeGroups: number; inProgress: number; emailsSent: number; emailsFailed: number; recipientsTargeted: number;
    },
    refetchInterval: 30000,
  });

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Mail size={22} className="text-indigo-600" /> Notifications</h1>
        <p className="text-sm text-slate-500">Send an HTML email — with attachments — to a group of merchants, and see exactly who received it.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Emails sent" value={(stats?.emailsSent ?? 0).toLocaleString()} tone="emerald" />
        <Stat label="Failed" value={(stats?.emailsFailed ?? 0).toLocaleString()} tone={stats?.emailsFailed ? 'rose' : 'slate'} />
        <Stat label="Campaigns" value={(stats?.campaigns ?? 0).toLocaleString()} hint={stats?.inProgress ? `${stats.inProgress} in progress` : undefined} />
        <Stat label="Active groups" value={(stats?.activeGroups ?? 0).toLocaleString()} tone="indigo" />
      </div>

      <div className="flex flex-wrap items-center gap-1 bg-slate-100 rounded-lg p-1 mb-4">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-sm rounded-md transition flex items-center gap-1.5 ${tab === t.id ? 'bg-white shadow-sm text-indigo-700 font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
            <t.Icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'send' && <ComposeTab onSent={() => setTab('log')} />}
      {tab === 'groups' && <GroupsTab />}
      {tab === 'templates' && <TemplatesTab />}
      {tab === 'log' && <SendLogTab />}
    </div>
  );
}

function Stat({ label, value, tone, hint }: { label: string; value: string; tone?: string; hint?: string }) {
  const c = tone === 'emerald' ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : tone === 'rose' ? 'border-rose-200 bg-rose-50 text-rose-700'
    : tone === 'indigo' ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
    : 'border-slate-200 bg-slate-50 text-slate-700';
  return (
    <div className={`p-3 rounded-xl border ${c}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
      {hint && <div className="text-[11px] opacity-70">{hint}</div>}
    </div>
  );
}

// ─────────────────────────── Groups ───────────────────────────

function useGroups() {
  return useQuery({
    queryKey: ['notif-groups'],
    queryFn: async () => (await api.get<Group[]>('/notifications/groups', { params: { includeInactive: 'true' } })).data,
  });
}

function GroupsTab() {
  const qc = useQueryClient();
  const { data: groups = [], isLoading } = useGroups();
  const [editing, setEditing] = useState<Group | 'new' | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);

  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/notifications/groups/${id}`)).data,
    onSuccess: (r: any) => { qc.invalidateQueries({ queryKey: ['notif-groups'] }); toast.success(r?.message ?? 'Group removed'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not remove the group'),
  });

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">Merchant Groups</h3>
        <button onClick={() => setEditing('new')} className="bg-indigo-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1.5">
          <Plus size={14} /> New Group
        </button>
      </div>
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="text-left px-3 py-2">Group</th>
            <th className="text-left px-3 py-2">Built from</th>
            <th className="text-right px-3 py-2">Merchants</th>
            <th className="text-right px-3 py-2">With email</th>
            <th className="text-left px-3 py-2">Created</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {isLoading && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Loading…</td></tr>}
          {!isLoading && groups.length === 0 && (
            <tr><td colSpan={6} className="text-center py-10 text-slate-400">
              No groups yet — create one to choose which merchants get your emails.
            </td></tr>
          )}
          {groups.map((g) => (
            <tr key={g.id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-3 py-2">
                <div className="font-medium text-slate-900 flex items-center gap-2">
                  {g.name}
                  {!g.isActive && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Inactive</span>}
                </div>
                {g.description && <div className="text-xs text-slate-500">{g.description}</div>}
              </td>
              <td className="px-3 py-2 text-xs text-slate-600">{g.states.length ? g.states.join(', ') : 'Hand-picked'}</td>
              <td className="px-3 py-2 text-right font-medium">{g.memberCount}</td>
              <td className="px-3 py-2 text-right">
                <span className={g.emailableCount < g.memberCount ? 'text-amber-700' : 'text-emerald-700'}>{g.emailableCount}</span>
                {g.emailableCount < g.memberCount && <div className="text-[10px] text-amber-600">{g.memberCount - g.emailableCount} without email</div>}
              </td>
              <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(g.createdAt)}{g.createdByName ? ` · ${g.createdByName}` : ''}</td>
              <td className="px-3 py-2">
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => setViewing(g.id)} title="View members" className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"><Eye size={14} /></button>
                  <button onClick={() => setEditing(g)} title="Edit group" className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded"><Pencil size={14} /></button>
                  <button onClick={() => { if (window.confirm(`Remove group "${g.name}"?`)) del.mutate(g.id); }}
                    title="Remove group" className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"><Trash2 size={14} /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && <GroupForm group={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {viewing != null && <GroupMembersModal groupId={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

/** Build/edit a group: filter merchants by state, then tick the ones to include. */
function GroupForm({ group, onClose }: { group: Group | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [states, setStates] = useState<string[]>(group?.states ?? []);
  const [selected, setSelected] = useState<Set<number>>(new Set(group?.merchantIds ?? []));
  const [search, setSearch] = useState('');

  const { data: stateOpts = [] } = useQuery({
    queryKey: ['notif-states'],
    queryFn: async () => (await api.get<{ state: string; merchantCount: number }[]>('/notifications/states')).data,
  });
  // The merchant list follows the chosen states; with none chosen it lists everyone.
  const { data: merchants = [], isLoading } = useQuery({
    queryKey: ['notif-merchant-options', states],
    queryFn: async () => (await api.get<MerchantOption[]>('/notifications/merchant-options', {
      params: states.length ? { states: states.join(',') } : {},
    })).data,
  });

  const visible = useMemo(() => {
    const t = search.trim().toLowerCase();
    return t ? merchants.filter((m) => [m.companyName, m.brandName, m.email, m.city].some((v) => String(v ?? '').toLowerCase().includes(t))) : merchants;
  }, [merchants, search]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = { name, description: description || null, states, merchantIds: Array.from(selected) };
      return group
        ? (await api.patch(`/notifications/groups/${group.id}`, payload)).data
        : (await api.post('/notifications/groups', payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notif-groups'] });
      qc.invalidateQueries({ queryKey: ['notif-stats'] });
      toast.success(group ? 'Group updated' : 'Group created');
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save the group'),
  });

  const toggleState = (s: string) =>
    setStates((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  const toggleMerchant = (id: number) =>
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // "Select all" applies to the rows currently VISIBLE, so a state filter or a search scopes it —
  // ticking it must never quietly add merchants the user cannot see.
  const allVisibleSelected = visible.length > 0 && visible.every((m) => selected.has(m.id));
  const someVisibleSelected = visible.some((m) => selected.has(m.id)) && !allVisibleSelected;
  // `indeterminate` is a DOM property, not an attribute, so React cannot set it from JSX.
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someVisibleSelected;
  }, [someVisibleSelected]);
  const toggleAllVisible = () =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) visible.forEach((m) => n.delete(m.id));
      else visible.forEach((m) => n.add(m.id));
      return n;
    });

  const noEmail = visible.filter((m) => selected.has(m.id) && !m.email).length;

  return (
    <Modal isOpen onClose={onClose} title={group ? `Edit group — ${group.name}` : 'New merchant group'}
      subtitle="Pick merchants by state, or search and tick them individually" maxWidth="4xl">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Group name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Maharashtra merchants" className="input w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Description</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What is this group for?" className="input w-full" />
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1.5">Filter by state <span className="text-slate-400 font-normal">(optional — leave empty for all merchants)</span></label>
          <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto border border-slate-200 rounded-lg p-2">
            {stateOpts.length === 0 && <span className="text-xs text-slate-400">No states on merchant records yet.</span>}
            {stateOpts.map((s) => (
              <button key={s.state} type="button" onClick={() => toggleState(s.state)}
                className={`px-2 py-1 rounded-full text-xs border transition ${states.includes(s.state) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
                {s.state} <span className="opacity-70">({s.merchantCount})</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <label className="text-xs font-medium text-slate-600">Merchants in this group</label>
            <span className="text-xs text-slate-500">{selected.size} selected</span>
            <div className="relative ml-auto">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search merchant / email"
                className="border border-slate-200 rounded pl-7 pr-2 py-1.5 text-xs w-56" />
            </div>
          </div>
          <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto">
            {/* The select-all was previously only a text link, which is not where anyone looks for it.
                Sticky so it stays reachable while scrolling a long merchant list. */}
            <label className="sticky top-0 z-10 flex items-center gap-2 px-2 py-2 bg-slate-50 border-b border-slate-200 cursor-pointer select-none">
              <input ref={selectAllRef} type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible}
                disabled={visible.length === 0} className="rounded" />
              <span className="text-xs font-medium text-slate-700">
                {allVisibleSelected ? 'Clear all' : 'Select all'}
                <span className="font-normal text-slate-500"> ({visible.length}{search || states.length ? ' shown' : ''})</span>
              </span>
            </label>
            {isLoading ? <div className="p-4 text-center text-xs text-slate-400">Loading merchants…</div>
              : visible.length === 0 ? <div className="p-4 text-center text-xs text-slate-400">No merchants match.</div>
              : (
                <table className="w-full text-xs">
                  <tbody>
                    {visible.map((m) => (
                      <tr key={m.id} onClick={() => toggleMerchant(m.id)}
                        className={`border-b border-slate-100 last:border-0 cursor-pointer ${selected.has(m.id) ? 'bg-indigo-50/60' : 'hover:bg-slate-50'}`}>
                        <td className="px-2 py-1.5 w-8"><input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleMerchant(m.id)} onClick={(e) => e.stopPropagation()} className="rounded" /></td>
                        <td className="px-2 py-1.5">
                          <div className="font-medium text-slate-800">{m.brandName || m.companyName}</div>
                          <div className="text-[11px] text-slate-500">#{m.id} · {m.state ?? '—'}{m.city ? `, ${m.city}` : ''}</div>
                        </td>
                        <td className="px-2 py-1.5">
                          {m.email
                            ? <span className="text-slate-600">{m.email}</span>
                            : <span className="text-amber-700">No email on record</span>}
                        </td>
                        <td className="px-2 py-1.5 text-right"><span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{m.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
          {noEmail > 0 && (
            <p className="text-[11px] text-amber-700 mt-1">{noEmail} selected merchant(s) have no email address — they'll be logged as skipped when you send.</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded">Cancel</button>
          <button onClick={() => save.mutate()} disabled={!name.trim() || selected.size === 0 || save.isPending}
            className="px-5 py-2 bg-indigo-600 text-white rounded text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
            {save.isPending && <Loader2 size={14} className="animate-spin" />}
            {group ? 'Save group' : `Create group (${selected.size})`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function GroupMembersModal({ groupId, onClose }: { groupId: number; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['notif-group', groupId],
    queryFn: async () => (await api.get<GroupDetail>(`/notifications/groups/${groupId}`)).data,
  });
  return (
    <Modal isOpen onClose={onClose} title={data?.name ?? 'Group'} subtitle={`${data?.memberCount ?? 0} merchants · ${data?.emailableCount ?? 0} with a valid email`} maxWidth="2xl">
      {isLoading ? <div className="py-8 text-center text-slate-400"><Loader2 size={18} className="animate-spin mx-auto" /></div> : (
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 sticky top-0"><tr>
              <th className="text-left px-2 py-1.5">Merchant</th><th className="text-left px-2 py-1.5">Email</th><th className="text-left px-2 py-1.5">State</th>
            </tr></thead>
            <tbody>
              {(data?.members ?? []).map((m) => (
                <tr key={m.id} className="border-t border-slate-100">
                  <td className="px-2 py-1.5">{m.brandName || m.companyName} <span className="text-slate-400">#{m.id}</span></td>
                  <td className="px-2 py-1.5">{m.email || <span className="text-amber-700">—</span>}</td>
                  <td className="px-2 py-1.5">{m.state ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ─────────────────────────── Attachments ───────────────────────────

/** Multi-file picker: uploads each file to R2 and keeps its ref. Enforces the 20 MB total. */
function AttachmentPicker({ attachments, onChange }: { attachments: Attachment[]; onChange: (a: Attachment[]) => void }) {
  const [busy, setBusy] = useState(false);
  const totalBytes = attachments.reduce((s, a) => s + Number(a.sizeBytes ?? 0), 0);

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    let running = totalBytes;
    const added: Attachment[] = [];
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        if (running + file.size > MAX_ATTACHMENT_BYTES) {
          toast.error(`"${file.name}" would push the total past 20 MB — skipped`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file, file.name);
        const res = await api.post('/notifications/attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        added.push({ ref: res.data.ref, fileName: res.data.fileName ?? file.name, mimetype: res.data.mimetype, sizeBytes: res.data.sizeBytes ?? file.size });
        running += file.size;
      }
      if (added.length) { onChange([...attachments, ...added]); toast.success(`${added.length} file(s) attached`); }
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Upload failed');
    } finally { setBusy(false); }
  };

  const pct = Math.min(100, Math.round((totalBytes / MAX_ATTACHMENT_BYTES) * 100));

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <label className="text-xs font-medium text-slate-600">Attachments</label>
        <span className="text-[11px] text-slate-500">{fmtBytes(totalBytes)} of 20 MB</span>
        <label className="ml-auto px-2.5 py-1 border border-slate-300 rounded text-xs hover:bg-slate-50 cursor-pointer flex items-center gap-1">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />} Add files
          <input type="file" multiple className="hidden" disabled={busy} onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} />
        </label>
      </div>
      {totalBytes > 0 && (
        <div className="h-1 bg-slate-100 rounded overflow-hidden mb-2">
          <div className={`h-full ${pct > 85 ? 'bg-amber-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {attachments.length === 0
        ? <p className="text-[11px] text-slate-400">No attachments. Up to 20 MB in total across all files.</p>
        : (
          <ul className="space-y-1">
            {attachments.map((a, i) => (
              <li key={a.ref + i} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                <Paperclip size={12} className="text-slate-400 shrink-0" />
                <span className="truncate flex-1">{a.fileName}</span>
                <span className="text-slate-400 shrink-0">{fmtBytes(a.sizeBytes)}</span>
                <button type="button" onClick={() => onChange(attachments.filter((_, j) => j !== i))}
                  title="Remove" className="text-slate-400 hover:text-rose-600 shrink-0"><X size={12} /></button>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}

// ─────────────────────────── Compose & send ───────────────────────────

function ComposeTab({ onSent }: { onSent: () => void }) {
  const qc = useQueryClient();
  const { data: groups = [] } = useGroups();
  const { data: templates = [] } = useQuery({
    queryKey: ['notif-templates'],
    queryFn: async () => (await api.get<Template[]>('/notifications/templates')).data,
  });

  const [groupId, setGroupId] = useState<number | ''>('');
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [preview, setPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const group = groups.find((g) => g.id === groupId);

  // Loading a template replaces the current draft with its content.
  const applyTemplate = (id: number | '') => {
    setTemplateId(id);
    if (id === '') return;
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setSubject(t.subject);
    setBodyHtml(t.bodyHtml);
    setAttachments((t.attachments ?? []) as Attachment[]);
    if (t.groupId) setGroupId(t.groupId);
    toast.success(`Loaded template "${t.name}"`);
  };

  const send = useMutation({
    mutationFn: async () => (await api.post('/notifications/campaigns', {
      groupId, templateId: templateId || null, subject, bodyHtml, attachments,
    })).data,
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['notif-campaigns'] });
      qc.invalidateQueries({ queryKey: ['notif-stats'] });
      toast.success(r?.message ?? 'Campaign queued');
      setConfirming(false);
      onSent();
    },
    onError: (e: any) => { setConfirming(false); toast.error(e?.response?.data?.message ?? 'Could not send'); },
  });

  const saveAsTemplate = useMutation({
    mutationFn: async (name: string) => (await api.post('/notifications/templates', {
      name, groupId: groupId || null, subject, bodyHtml, attachments,
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notif-templates'] }); toast.success('Saved as template'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save template'),
  });

  const canSend = !!groupId && !!subject.trim() && !!bodyHtml.trim() && (group?.emailableCount ?? 0) > 0;

  return (
    <div className="grid lg:grid-cols-[1fr_20rem] gap-4">
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Send to group *</label>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value ? Number(e.target.value) : '')} className="input w-full">
              <option value="">Choose a merchant group…</option>
              {groups.filter((g) => g.isActive).map((g) => (
                <option key={g.id} value={g.id}>{g.name} — {g.emailableCount} merchant(s)</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Start from a template</label>
            <select value={templateId} onChange={(e) => applyTemplate(e.target.value ? Number(e.target.value) : '')} className="input w-full">
              <option value="">Blank email</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.group ? ` (${t.group.name})` : ''}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Subject *</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Revised COD remittance schedule from 1 August" className="input w-full" />
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Body *</label>
          <RichTextEditor value={bodyHtml} onChange={setBodyHtml} placeholder="Write your message to merchants…" />
        </div>

        <AttachmentPicker attachments={attachments} onChange={setAttachments} />

        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <button onClick={() => setPreview(true)} disabled={!bodyHtml.trim()}
            className="px-3 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5">
            <Eye size={14} /> Preview
          </button>
          <button
            onClick={() => {
              const name = window.prompt('Save this email as a template — name it:');
              if (name?.trim()) saveAsTemplate.mutate(name.trim());
            }}
            disabled={!subject.trim() || !bodyHtml.trim() || saveAsTemplate.isPending}
            className="px-3 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5">
            <FileText size={14} /> Save as template
          </button>
          <button onClick={() => setConfirming(true)} disabled={!canSend}
            className="ml-auto px-5 py-2 bg-indigo-600 text-white rounded text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
            <Send size={14} /> Send to {group?.emailableCount ?? 0} merchant(s)
          </button>
        </div>
      </div>

      {/* Recipient summary — who is actually going to get this */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 h-fit">
        <h3 className="font-semibold text-sm mb-2 flex items-center gap-1.5"><Users size={14} /> Recipients</h3>
        {!group ? (
          <p className="text-xs text-slate-400">Pick a group to see who this email goes to.</p>
        ) : (
          <div className="space-y-2 text-xs">
            <div className="flex justify-between"><span className="text-slate-500">Group</span><span className="font-medium text-right">{group.name}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Merchants</span><span className="font-medium">{group.memberCount}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Will receive</span><span className="font-medium text-emerald-700">{group.emailableCount}</span></div>
            {group.memberCount > group.emailableCount && (
              <div className="flex justify-between"><span className="text-slate-500">Skipped (no email)</span><span className="font-medium text-amber-700">{group.memberCount - group.emailableCount}</span></div>
            )}
            {group.states.length > 0 && (
              <div className="pt-2 border-t border-slate-100">
                <div className="text-slate-500 mb-1">States</div>
                <div className="flex flex-wrap gap-1">{group.states.map((s) => <span key={s} className="px-1.5 py-0.5 rounded bg-slate-100">{s}</span>)}</div>
              </div>
            )}
            <div className="pt-2 border-t border-slate-100 text-slate-500">
              Sent from Gaint Logistics' configured SMTP sender. Every recipient's outcome is recorded in the Send Log.
            </div>
          </div>
        )}
      </div>

      {preview && (
        <Modal isOpen onClose={() => setPreview(false)} title={subject || '(no subject)'} subtitle="Preview — this is what merchants will see" maxWidth="3xl">
          <div className="border border-slate-200 rounded-lg p-4 max-h-[60vh] overflow-y-auto prose-email"
            dangerouslySetInnerHTML={{ __html: bodyHtml }} />
          {attachments.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-xs text-slate-500 mb-1">{attachments.length} attachment(s)</div>
              <ul className="text-xs space-y-1">
                {attachments.map((a, i) => <li key={i} className="flex items-center gap-1.5"><Paperclip size={11} className="text-slate-400" /> {a.fileName} <span className="text-slate-400">({fmtBytes(a.sizeBytes)})</span></li>)}
              </ul>
            </div>
          )}
        </Modal>
      )}

      {confirming && group && (
        <Modal isOpen onClose={() => setConfirming(false)} title="Send this email?" subtitle="This goes out immediately and can't be recalled" maxWidth="md">
          <div className="space-y-3 text-sm">
            <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-1.5">
              <div className="flex justify-between"><span className="text-slate-500">To</span><span className="font-medium">{group.emailableCount} merchant(s) in “{group.name}”</span></div>
              <div className="flex justify-between gap-3"><span className="text-slate-500 shrink-0">Subject</span><span className="font-medium text-right truncate">{subject}</span></div>
              {attachments.length > 0 && (
                <div className="flex justify-between"><span className="text-slate-500">Attachments</span><span className="font-medium">{attachments.length} · {fmtBytes(attachments.reduce((s, a) => s + Number(a.sizeBytes ?? 0), 0))}</span></div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setConfirming(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded">Cancel</button>
              <button onClick={() => send.mutate()} disabled={send.isPending}
                className="px-5 py-2 bg-indigo-600 text-white rounded text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
                {send.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send now
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────── Templates ───────────────────────────

function TemplatesTab() {
  const qc = useQueryClient();
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['notif-templates'],
    queryFn: async () => (await api.get<Template[]>('/notifications/templates')).data,
  });
  const [editing, setEditing] = useState<Template | 'new' | null>(null);

  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/notifications/templates/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notif-templates'] }); toast.success('Template deleted'); },
  });

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold">Email Templates</h3>
          <p className="text-xs text-slate-500">Reusable subject + body (+ attachments), optionally tied to a group.</p>
        </div>
        <button onClick={() => setEditing('new')} className="bg-indigo-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1.5">
          <Plus size={14} /> New Template
        </button>
      </div>
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
          <th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Subject</th>
          <th className="text-left px-3 py-2">Group</th><th className="text-right px-3 py-2">Files</th>
          <th className="text-left px-3 py-2">Updated</th><th className="px-3 py-2" />
        </tr></thead>
        <tbody>
          {isLoading && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Loading…</td></tr>}
          {!isLoading && templates.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400">No templates yet.</td></tr>}
          {templates.map((t) => (
            <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-3 py-2 font-medium text-slate-900">{t.name}</td>
              <td className="px-3 py-2 text-slate-600 max-w-[320px] truncate" title={t.subject}>{t.subject}</td>
              <td className="px-3 py-2 text-xs text-slate-500">{t.group?.name ?? '—'}</td>
              <td className="px-3 py-2 text-right text-xs">{(t.attachments ?? []).length || '—'}</td>
              <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(t.updatedAt)}</td>
              <td className="px-3 py-2">
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => setEditing(t)} title="Edit" className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded"><Pencil size={14} /></button>
                  <button onClick={() => { if (window.confirm(`Delete template "${t.name}"?`)) del.mutate(t.id); }}
                    title="Delete" className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"><Trash2 size={14} /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editing && <TemplateForm template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function TemplateForm({ template, onClose }: { template: Template | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: groups = [] } = useGroups();
  const [name, setName] = useState(template?.name ?? '');
  const [groupId, setGroupId] = useState<number | ''>(template?.groupId ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [bodyHtml, setBodyHtml] = useState(template?.bodyHtml ?? '');
  const [attachments, setAttachments] = useState<Attachment[]>((template?.attachments ?? []) as Attachment[]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = { name, groupId: groupId || null, subject, bodyHtml, attachments };
      return template
        ? (await api.patch(`/notifications/templates/${template.id}`, payload)).data
        : (await api.post('/notifications/templates', payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notif-templates'] }); toast.success(template ? 'Template updated' : 'Template created'); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save the template'),
  });

  return (
    <Modal isOpen onClose={onClose} title={template ? `Edit — ${template.name}` : 'New email template'} maxWidth="4xl">
      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Template name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="input w-full" placeholder="e.g. Diwali holiday notice" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">For group</label>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value ? Number(e.target.value) : '')} className="input w-full">
              <option value="">Any group</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Subject *</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="input w-full" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Body *</label>
          <RichTextEditor value={bodyHtml} onChange={setBodyHtml} minHeight={220} />
        </div>
        <AttachmentPicker attachments={attachments} onChange={setAttachments} />
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded">Cancel</button>
          <button onClick={() => save.mutate()} disabled={!name.trim() || !subject.trim() || !bodyHtml.trim() || save.isPending}
            className="px-5 py-2 bg-indigo-600 text-white rounded text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
            {save.isPending && <Loader2 size={14} className="animate-spin" />} Save template
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────── Send log ───────────────────────────

function SendLogTab() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);
  const pageSize = 25;

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['notif-campaigns', search, status, page],
    queryFn: async () => (await api.get<{ total: number; items: Campaign[] }>('/notifications/campaigns', {
      params: { ...(search ? { search } : {}), ...(status ? { status } : {}), page, pageSize },
    })).data,
    // Keep the progress bars moving while a blast is running.
    refetchInterval: (q: any) => (q?.state?.data?.items ?? []).some((c: Campaign) => c.status === 'QUEUED' || c.status === 'SENDING') ? 5000 : false,
    placeholderData: (prev: any) => prev,
  });
  const campaigns = data?.items ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  const statusTone = (s: string) =>
    s === 'COMPLETED' ? 'bg-emerald-100 text-emerald-700'
    : s === 'SENDING' || s === 'QUEUED' ? 'bg-blue-100 text-blue-700'
    : s === 'FAILED' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600';

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <h3 className="font-semibold">Send Log</h3>
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search subject / group / campaign no"
            className="border border-slate-200 rounded pl-7 pr-2 py-1.5 text-xs w-64" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="border border-slate-200 rounded px-2 py-1.5 text-xs">
          <option value="">All statuses</option>
          <option value="QUEUED">Queued</option>
          <option value="SENDING">Sending</option>
          <option value="COMPLETED">Completed</option>
          <option value="FAILED">Failed</option>
        </select>
        <button onClick={() => refetch()} className="ml-auto px-2.5 py-1.5 border border-slate-200 rounded text-xs hover:bg-slate-50 flex items-center gap-1">
          <RefreshCcw size={12} className={isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
            <th className="text-left px-3 py-2">Campaign</th><th className="text-left px-3 py-2">Group</th>
            <th className="text-left px-3 py-2">Subject</th>
            <th className="text-right px-3 py-2">Sent</th><th className="text-right px-3 py-2">Failed</th>
            <th className="text-right px-3 py-2">Skipped</th>
            <th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Sent by / when</th>
            <th className="px-3 py-2" />
          </tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={9} className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!isLoading && campaigns.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-slate-400">Nothing sent yet.</td></tr>}
            {campaigns.map((c) => {
              const done = c.sentCount + c.failedCount + c.skippedCount;
              const pct = c.totalRecipients ? Math.round((done / c.totalRecipients) * 100) : 0;
              return (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">{c.campaignNo}
                    {(c.attachments ?? []).length > 0 && <span className="ml-1.5 text-slate-400" title={`${(c.attachments ?? []).length} attachment(s) · ${fmtBytes(c.attachmentBytes)}`}><Paperclip size={11} className="inline" /></span>}
                  </td>
                  <td className="px-3 py-2 text-xs">{c.groupName ?? '—'}</td>
                  <td className="px-3 py-2 max-w-[280px] truncate" title={c.subject}>{c.subject}</td>
                  <td className="px-3 py-2 text-right font-medium text-emerald-700">{c.sentCount}</td>
                  <td className={`px-3 py-2 text-right font-medium ${c.failedCount ? 'text-rose-700' : 'text-slate-300'}`}>{c.failedCount || '—'}</td>
                  <td className={`px-3 py-2 text-right ${c.skippedCount ? 'text-amber-700' : 'text-slate-300'}`}>{c.skippedCount || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded ${statusTone(c.status)}`}>{c.status}</span>
                    {(c.status === 'SENDING' || c.status === 'QUEUED') && (
                      <div className="h-1 bg-slate-100 rounded mt-1 overflow-hidden w-20"><div className="h-full bg-blue-500" style={{ width: `${pct}%` }} /></div>
                    )}
                    <div className="text-[10px] text-slate-400">{done}/{c.totalRecipients}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{c.sentByName ?? '—'}<div className="text-[10px]">{fmtDate(c.completedAt ?? c.startedAt ?? c.createdAt)}</div></td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setOpenId(c.id)} className="px-2 py-1 border border-slate-200 rounded text-xs hover:bg-slate-100 whitespace-nowrap">
                      View log
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {total > pageSize && (
        <div className="flex items-center justify-end gap-2 mt-3 text-xs text-slate-600">
          <span>{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-2 py-1 border rounded disabled:opacity-40">‹</button>
          <button disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 border rounded disabled:opacity-40">›</button>
        </div>
      )}

      {openId != null && <CampaignLogModal id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

/** Per-merchant delivery detail: how many emails went out, to which merchant address, and why any failed. */
function CampaignLogModal({ id, onClose }: { id: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['notif-campaign', id],
    queryFn: async () => (await api.get(`/notifications/campaigns/${id}`)).data as Campaign & { logs: CampaignLog[]; logCounts: Record<string, number>; bodyHtml: string },
    refetchInterval: (q: any) => ['QUEUED', 'SENDING'].includes(q?.state?.data?.status) ? 4000 : false,
  });

  const retry = useMutation({
    mutationFn: async () => (await api.post(`/notifications/campaigns/${id}/retry`)).data,
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['notif-campaign', id] });
      qc.invalidateQueries({ queryKey: ['notif-campaigns'] });
      toast.success(`Re-queued ${r?.requeued ?? 0} failed recipient(s)`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Retry failed'),
  });

  const logs = (data?.logs ?? []).filter((l) => !filter || l.status === filter);
  const icon = (s: string) => s === 'SENT' ? <CheckCircle2 size={12} className="text-emerald-600" />
    : s === 'FAILED' ? <XCircle size={12} className="text-rose-600" />
    : <MinusCircle size={12} className="text-amber-600" />;

  return (
    <Modal isOpen onClose={onClose} title={`${data?.campaignNo ?? 'Campaign'} — ${data?.subject ?? ''}`}
      subtitle={data ? `${data.sentCount} sent · ${data.failedCount} failed · ${data.skippedCount} skipped of ${data.totalRecipients} merchants` : undefined}
      maxWidth="4xl">
      {isLoading ? <div className="py-8 text-center text-slate-400"><Loader2 size={18} className="animate-spin mx-auto" /></div> : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-slate-200 rounded px-2 py-1.5">
              <option value="">All recipients ({data?.logs.length ?? 0})</option>
              {Object.entries(data?.logCounts ?? {}).map(([s, n]) => <option key={s} value={s}>{s} ({n})</option>)}
            </select>
            {(data?.attachments ?? []).length > 0 && (
              <span className="text-slate-500 flex items-center gap-1"><Paperclip size={11} /> {(data?.attachments ?? []).length} file(s) · {fmtBytes(data?.attachmentBytes)}</span>
            )}
            {(data?.failedCount ?? 0) > 0 && (
              <button onClick={() => retry.mutate()} disabled={retry.isPending}
                className="px-2.5 py-1.5 border border-amber-300 text-amber-700 rounded hover:bg-amber-50 flex items-center gap-1 disabled:opacity-50">
                {retry.isPending ? <Loader2 size={11} className="animate-spin" /> : <RefreshCcw size={11} />} Retry failed
              </button>
            )}
            <button onClick={() => downloadBlob(`/notifications/campaigns/${id}/export`, `email-campaign-${data?.campaignNo ?? id}.csv`)}
              className="ml-auto px-2.5 py-1.5 border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1">
              <Download size={11} /> Export CSV
            </button>
          </div>

          {data?.errorMessage && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">{data.errorMessage}</div>
          )}

          <div className="border border-slate-200 rounded-lg max-h-[55vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 sticky top-0"><tr>
                <th className="text-left px-2 py-1.5">Merchant</th><th className="text-left px-2 py-1.5">Email</th>
                <th className="text-left px-2 py-1.5">Result</th><th className="text-left px-2 py-1.5">When / reason</th>
              </tr></thead>
              <tbody>
                {logs.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-400">No recipients match.</td></tr>}
                {logs.map((l) => (
                  <tr key={l.id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5">{l.merchantName ?? '—'} {l.merchantId ? <span className="text-slate-400">#{l.merchantId}</span> : null}</td>
                    <td className="px-2 py-1.5 font-mono">{l.email ?? '—'}</td>
                    <td className="px-2 py-1.5"><span className="inline-flex items-center gap-1">{icon(l.status)} {l.status}</span></td>
                    <td className="px-2 py-1.5 text-slate-500">
                      {l.status === 'SENT' ? fmtDate(l.sentAt) : (l.error ?? '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
